/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/info.scss';

import React, {CSSProperties, MouseEventHandler, PureComponent} from 'react';
import {ControlContext} from "../util/controlContext";
import {colorIsWhite, convertColor, editColor} from "../util/colorUtil";
import classNames from 'classnames';
import {getScreenHeight, getScreenWidth} from '../util/responsiveUtil';
import { directTimeout } from '../util/componentUtil';
import AwesomeIcon from '../icon/awesomeIcon';

type LimitBox = Partial<{ top: number, bottom: number, left: number, right: number }>;

interface Props {
    onClick?: MouseEventHandler;
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    message: React.ReactNode;
    title?: string;
    className?: string;
    messageClassName?: string;
    iconClassName?: string;
    disabled?: boolean;
    limitBox?: LimitBox;
}

interface State {
    hovered: boolean;
}

export default class Info extends PureComponent<Props, State> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    private messageContainer: HTMLDivElement;

    state: State = {
        hovered: false,
    };

    getColor = (): string => {
        const {baseColor, contrastColor} = this.context, {hovered} = this.state, {disabled} = this.props;
        let color = convertColor(baseColor);
        if (colorIsWhite(color)) color = convertColor(contrastColor);
        if (hovered) color = editColor(-.2, color);
        if (disabled) color = '#ccc';
        return color;
    };

    onMouseEnter = (evt: React.MouseEvent) => {
        const {onMouseEnter} = this.props;
        this.setState({hovered: true});
        if (onMouseEnter) onMouseEnter(evt);
    };

    onMouseLeave = (evt: React.MouseEvent) => {
        const {onMouseLeave} = this.props;
        this.setState({hovered: false});
        if (onMouseLeave) onMouseLeave(evt);
    };

    getIconStyle = (): CSSProperties => {
        return {color: this.getColor()};
    };

    setMessagePosition = () => {
        if (this.messageContainer) {
            let {width, height, bottom, left, right, top} = this.messageContainer.getBoundingClientRect(),
                screenWidth = getScreenWidth(), screenHeight = getScreenHeight(), {limitBox} = this.props;
            top = top - (limitBox?.top || 0);
            left = left - (limitBox?.left || 0);
            bottom = bottom + (limitBox?.bottom || 0);
            right = right + (limitBox?.right || 0);
            let translateX: number = 0, translateY: number = 0;
            if (top < 0) translateY = top;
            else if (bottom > screenHeight) translateY = screenHeight - bottom;
            if (left < 0) translateX = left;
            else if (right > screenWidth) translateX = screenWidth - right;

            let transform: string = this.messageContainer.style.transform;
            const {x, y} = this.getCurrentTranslates(transform);
            if (translateX) {
                translateX = this.getCalculatedTranslate(x, translateX, width);
                transform = transform.replace(/translateX\(-?\d*(%|px|rem)?\)/, `translateX(${translateX}%)`);
            }
            if (translateY) {
                translateY = this.getCalculatedTranslate(y, translateY, height);
                transform = transform.replace(/translateY\(-?\d*(%|px|rem)?\)/, `translateY(${translateY}%)`);
            }
            this.messageContainer.style.transform = transform;
        }
    };

    getCalculatedTranslate = (initial: number, value: number, dimension: number) => {
        return initial - Math.round((value / dimension) * 100);
    };

    getCurrentTranslates = (transform: string): { x: number, y: number } => {
        let x = 100, y = -100;
        const translateX = transform.match(/translateX\(-?\d*(%|px|rem)?\)/),
            translateY = transform.match(/translateY\(-?\d*(%|px|rem)?\)/);
        if (translateX) x = Number(translateX[0].match(/\(-?\d*(%|px|rem)?\)/)![0].replace(/[()%]|rem|px/g, ''));
        if (translateY) y = Number(translateY[0].match(/\(-?\d*(%|px|rem)?\)/)![0].replace(/[()%]|rem|px/g, ''));
        return {x, y};
    };

    renderMessage = (): React.ReactNode => {
        const {message} = this.props;
        if (typeof message === 'string') return <p className='dvr-info-message'>{message}</p>;
        return message;
    };

    componentDidUpdate = (prevProps: Props, prevState: State) => {
        const {hovered} = this.state, prevHovered = prevState.hovered;
        if (hovered && hovered !== prevHovered)
            directTimeout(this.setMessagePosition);
    };

    render = () => {
        const {className, messageClassName, iconClassName} = this.props;
        return (
            <div className={classNames('dvr-info-container', className)} onMouseEnter={this.onMouseEnter}
                 onMouseLeave={this.onMouseLeave}>
                <AwesomeIcon name='info-circle' style={this.getIconStyle()}
                             className={classNames('dvr-info-icon', iconClassName)}/>
                <div className={classNames('dvr-message-container', messageClassName)}
                     ref={(ref: HTMLDivElement) => {this.messageContainer = ref}}
                     style={{transform: 'translateX(100%) translateY(-100%)'}}>
                    {this.renderMessage()}
                </div>
            </div>
        )
    };
};